<template>
  <page-template :is-loading="isLoading" :data-exists="serverGroups !== null">
    <template #pageHeadline>
      {{ $t('serverGroup.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="serverGroups"
        :sticky-header="true"
        :row-buttons="rowButtons"
        :new-button-link="getNewServerGroupPath()"
        class="rbz-list-table"
        data-qa="server-groups-table"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateServerGroup"
        @delete-row-button-clicked="openDeleteServerGroupModal($event)"
        @download-data="downloadDoc"
      >
        <template #colServerNames="cellProps">
          <span
            v-if="cellProps.row.server_names.length > 1"
            class="show-more-wrapper"
            @click.stop.prevent="showServerNames(cellProps.row)"
          >
            <span class="server-name ellipsis">{{ cellProps.row.server_names[0] }}</span>
            <a href="#" class="show-more">show {{ cellProps.row.server_names.length - 1 }} more</a>
          </span>
          <span v-else class="server-name ellipsis" v-b-tooltip="cellProps.row.server_names[0]">{{
            cellProps.row.server_names[0]
          }}</span>
        </template>
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteServerGroupModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        :primary-btn-disabled="!isDeleteServerGroupNameValid"
        @onPrimaryBtnClick="deleteServerGroup"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteServerGroupModal = false"
        @closeModal="showDeleteServerGroupModal = false"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="delete-server-group-name-input"
              data-qa="delete-server-group-name-input"
              :label="$t('serverGroup.deleteConfirm')"
              :placeholder="$t('serverGroup.deleteConfirmPlaceholder')"
              v-model:inputValue="deleteServerGroupName"
            />
          </div>
        </template>
      </rbz-modal-dialog>
      <rbz-offcanvas v-model:showOffcanvas="showServerNamesModal">
        <template #header>
          {{ $t('serverGroup.listPage.hostAuthorityHeader') }}
        </template>
        <template #content>
          <div class="server-names-table-wrapper">
            <rbz-table
              :columns="serverNamesColumns"
              class="server-names-table"
              :show-new-button="false"
              :table-data="serverGroupNames"
              rows-per-page="all"
              :show-download-button="false"
              :show-filter-row="true"
              :hide-header-row="true"
              :show-narrow-column="false"
              :show-top-navbar="true"
              :show-buttons-column="false"
            >
            </rbz-table>
          </div>
        </template>
        <template #footer>
          <rbz-form-button
            class="rbz-offcanvas-footer-button"
            :label="$t('common.operations.cancel.label')"
            @click="showServerNamesModal = false"
            variant="secondary"
          />
          <rbz-form-button
            class="rbz-offcanvas-footer-button"
            :label="$t('serverGroup.listPage.editServerGroup')"
            @click="editServerGroup(selectedServerGroup)"
            variant="primary"
          />
        </template>
      </rbz-offcanvas>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, ProxyTemplate, RoutingProfile, SecurityPolicy, ServerGroup } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import {
  GenericObject,
  RbzTableColumnOptions,
  RbzTableRowButton,
} from 'ui-component-library/rbz/types';
import RbzOffcanvas from '@/components/RbzOffcanvas.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ServerGroupsList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    RbzFormInput,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
    RbzOffcanvas,
    RbzFormButton,
  },

  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('serverGroup.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getServerGroupPath(item);
          },
        },
        {
          id: 'security-policy',
          title: this.$t('serverGroup.securityPolicy'),
          fieldNames: ['security_policy'],
          displayFunction: (item: ServerGroup) => {
            const securityPolicy = _.find(this.securityPoliciesNames, (securityPolicy) => {
              return securityPolicy[0] === item.security_policy;
            });
            return securityPolicy?.[1] || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-300px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'proxy-template',
          title: this.$t('serverGroup.proxyTemplate'),
          fieldNames: ['proxy_template'],
          displayFunction: (item: ServerGroup) => {
            const proxyTemplate = _.find(this.proxyTemplatesNames, (proxyTemplate) => {
              return proxyTemplate[0] === item.proxy_template;
            });
            return proxyTemplate?.[1] || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-300px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'server_names',
          title: this.$t('serverGroup.listPage.hostAuthorityHeader'),
          fieldNames: ['server_names'],
          displaySlotName: 'colServerNames',
          displayFunction: (item) => {
            return item.server_names.join('\n');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-260px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getServerGroupPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      serverGroups: null as ServerGroup[],
      pageLoadingCounter: null,

      securityPoliciesNames: [] as [SecurityPolicy['id'], SecurityPolicy['name']][],
      routingProfilesNames: [] as [RoutingProfile['id'], RoutingProfile['name']][],
      proxyTemplatesNames: [] as [ProxyTemplate['id'], ProxyTemplate['name']][],

      showDeleteServerGroupModal: false,
      selectedServerGroupId: null,
      selectedServerGroupName: '',
      deleteServerGroupName: '',
      showServerNamesModal: false,
      selectedServerGroup: null as ServerGroup,
      serverGroupNames: [] as GenericObject[],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Server Groups List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadServerGroups();
          await this.loadSecurityPolicies();
          await this.loadProxyTemplates();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('serverGroup.plural'),
        singular: this.$t('serverGroup.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    isDeleteServerGroupNameValid(): boolean {
      const inputName = this.deleteServerGroupName.trim();
      return inputName === this.selectedServerGroupName;
    },

    serverNamesColumns(): RbzTableColumnOptions[] {
      return [
        {
          id: 'server_names',
          title: this.$t('serverGroup.listPage.hostAuthorityHeader'),
          fieldNames: ['server_names'],
          displayFunction: (item) => {
            return item.name;
          },
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
      ];
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    getNewServerGroupPath() {
      return this.getNewPath('server-groups');
    },

    isDeleteDisabled(serverGroup: ServerGroup) {
      return this.isServerGroupProtected(serverGroup);
    },

    isServerGroupProtected(serverGroup: ServerGroup): boolean {
      return serverGroup.id.startsWith('__');
    },

    deleteTooltip(serverGroup: ServerGroup): string {
      if (this.isServerGroupProtected(serverGroup)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getServerGroupPath(serverGroup: ServerGroup) {
      return this.getEditPath('server-groups', serverGroup.id);
    },

    editServerGroup(serverGroup: ServerGroup) {
      this.navigateTo(this.getServerGroupPath(serverGroup));
    },

    async duplicateServerGroup(serverGroup: ServerGroup) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups/${serverGroup.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalServerGroup = getOriginalResponse.data;
      const serverGroupToAdd = _.cloneDeep(originalServerGroup);
      serverGroupToAdd.id = DatasetsUtils.generateUUID2();
      serverGroupToAdd.name = Utils.generateUniqueEntityName(
        serverGroupToAdd.name,
        _.map(this.serverGroups, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/server-groups/${serverGroupToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: serverGroupToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editServerGroup(serverGroupToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteServerGroup() {
      this.setPageLoadingStatus(true);
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/server-groups/${this.selectedServerGroupId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.serverGroups = _.filter(this.serverGroups, (serverGroup) => {
          return serverGroup.id !== this.selectedServerGroupId;
        });
      }
      this.showDeleteServerGroupModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('server-groups', 'json', this.serverGroups);
    },

    async loadServerGroups() {
      const url = `conf/${this.selectedBranch}/server-groups`;
      const host = this.userPlanet;
      const response = await RequestsUtils.sendRequest({ methodName: 'GET', url, host });
      this.serverGroups = response?.data?.items || [];
    },

    async loadSecurityPolicies() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.securityPoliciesNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },

    async loadProxyTemplates() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/proxy-templates`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.proxyTemplatesNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },

    openDeleteServerGroupModal(serverGroup: ServerGroup) {
      this.showDeleteServerGroupModal = true;
      this.selectedServerGroupId = serverGroup.id;
      this.selectedServerGroupName = serverGroup.name;
      this.deleteServerGroupName = '';
    },

    showServerNames(serverGroup: ServerGroup) {
      this.showServerNamesModal = true;
      this.selectedServerGroupId = serverGroup.id;
      this.selectedServerGroup = serverGroup;
      this.serverGroupNames = serverGroup.server_names.map((name) => ({
        name: name,
      }));
    },
  },
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.rbz-offcanvas-footer-button {
  padding: 10px 0 10px 10px;
}

.show-more-wrapper {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  align-items: center;

  .server-name {
    width: 55%;
  }
}

.server-name {
  display: block;
}

.server-names-table-wrapper :deep(.server-names-table) {
  td {
    border-bottom: 0;
  }

  .filter-cell {
    padding: 16px 0;
    width: 100%;
  }

  .data-row:hover {
    background-color: transparent;
  }

  .data-row .row-buttons {
    opacity: 0;
  }

  .data-cell {
    padding: 0;
    height: 0;
  }

  .data-cell .data-cell-content {
    padding: 0;
  }

  .filter-table-data-button .rbz-button {
    height: 36px;
    padding: 6px;
  }

  .table-results-wrapper {
    padding: 8px 12px;
  }

  .table-buttons {
    margin: 0 16px;
  }

  .table-buttons-icons {
    gap: 0;
  }
}
</style>
